// Plain DOM helpers for the panels. Colours in the scene are linear; the pickers show sRGB.

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', html = ''): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html) e.innerHTML = html;
  return e;
}

export function button(label: string, onClick: () => void, cls = 'btn'): HTMLButtonElement {
  const b = el('button', cls, label);
  b.type = 'button';
  b.addEventListener('click', onClick);
  return b;
}

const NS = 'http://www.w3.org/2000/svg';
const svg = (tag: string, attrs: Record<string, string | number>, ...kids: Element[]) => {
  const n = document.createElementNS(NS, tag);
  for (const k in attrs) n.setAttribute(k, String(attrs[k]));
  n.append(...kids);
  return n;
};

let gooDefs = false;
function defs() {
  if (gooDefs) return;
  gooDefs = true;
  const d = svg('svg', { width: 0, height: 0, 'aria-hidden': 'true', style: 'position:absolute' }, svg('defs', {},
    svg('filter', { id: 'goo-f', x: '-10%', y: '-10%', width: '120%', height: '140%' },
      svg('feGaussianBlur', { in: 'SourceGraphic', stdDeviation: 3.2, result: 'b' }),
      svg('feColorMatrix', { in: 'b', values: '1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -10' })),
    svg('linearGradient', { id: 'goo-g', x1: 0, y1: 0, x2: 0, y2: 1 },
      svg('stop', { offset: 0, 'stop-color': '#9fe857' }), svg('stop', { offset: 0.55, 'stop-color': '#55bd25' }), svg('stop', { offset: 1, 'stop-color': '#2c8512' })),
    svg('linearGradient', { id: 'goo-s', x1: 0, y1: 0, x2: 0, y2: 1 },
      svg('stop', { offset: 0, 'stop-color': '#fff', 'stop-opacity': 0.75 }), svg('stop', { offset: 1, 'stop-color': '#fff', 'stop-opacity': 0 }))));
  document.body.prepend(d);
}

/**
 * Turns a button into goo: an SVG silhouette, body and drips in one blob. The drips are stalks that stretch with a drop at the
 * tip; the blur and threshold merge them into the body's edge, and now and then a drop lets go and falls.
 * viewBox 200 x 150: the body is the top 100, the rest is where the drips hang.
 */
export function gooDrips(b: HTMLElement) {
  if (b.querySelector('.goo-svg')) return;
  defs();
  if (b.firstChild?.nodeType === Node.TEXT_NODE) { const t = el('span'); t.textContent = b.firstChild.textContent; b.firstChild.replaceWith(t); }
  // x, width, stalk length, period, delay
  const drips: [number, number, number, number, number][] = [[28, 9, 30, 7.3, 0], [76, 11, 38, 9.1, 2.6], [124, 8, 26, 6.4, 4.1], [168, 10, 34, 8.2, 1.2]];
  const fill = { fill: 'url(#goo-g)' };
  const body = svg('path', { ...fill, d: 'M16 2 C60 -3 140 -3 184 2 C197 4 199 12 198 26 C200 48 199 72 197 88 C196 96 190 100 180 100 L20 100 C10 100 3 96 3 88 C1 70 2 46 2 26 C2 12 5 4 16 2 Z' });
  const sags = [[12, 7], [52, 5], [100, 6], [146, 5], [186, 6]].map(([x, r], i) =>
    svg('ellipse', { ...fill, class: 'gd-sag', cx: x, cy: 99, rx: r, ry: r * 0.9, style: `--t:${5 + i * 0.9}s;--d:-${i * 1.3}s` }));
  const parts = drips.flatMap(([x, w, l, t, d]) => {
    const st = `--t:${t}s;--d:-${d}s;--l:${l}px`;
    return [svg('rect', { ...fill, class: 'gd-stalk', x: x - w / 2, y: 92, width: w, height: l, rx: w / 2, style: st }),
      svg('circle', { ...fill, class: 'gd-drop', cx: x, cy: 94, r: w * 0.85, style: st })];
  });
  const blob = svg('g', { filter: 'url(#goo-f)' }, body, ...sags, ...parts);
  const shine = svg('path', { d: 'M22 14 C60 8 120 8 150 13 C120 22 60 24 22 28 Z', fill: 'url(#goo-s)', opacity: 0.8 });
  const shade = svg('path', { d: 'M10 84 C60 96 140 96 190 84 L190 92 C190 98 184 99 176 99 L24 99 C14 99 10 96 10 92 Z', fill: '#124a05', opacity: 0.28 });
  b.prepend(svg('svg', { class: 'goo-svg', viewBox: '0 0 200 150', preserveAspectRatio: 'none', 'aria-hidden': 'true' }, blob, shade, shine));
}

/** the house action button: one blob of green goo, dripping */
export function goo(label: string, onClick: () => void, cls = ''): HTMLButtonElement {
  const b = button(label, onClick, ('goo ' + cls).trim());
  gooDrips(b);
  return b;
}

const toS = (c: number) => { c = Math.max(0, Math.min(1, c)); return c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055; };
const toL = (c: number) => c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
export const hexOf = (c: [number, number, number]) => '#' + c.map(x => Math.round(toS(x) * 255).toString(16).padStart(2, '0')).join('');
export const linOf = (h: string): [number, number, number] => [1, 3, 5].map(i => toL(parseInt(h.slice(i, i + 2), 16) / 255)) as [number, number, number];

/** a labelled slider; `get` is read again on refresh() so the panel can follow the scene */
export interface Control { root: HTMLElement; refresh(): void }

export function slider(label: string, min: number, max: number, step: number, get: () => number, set: (v: number) => void, fmt = (v: number) => v.toFixed(2)): Control {
  const root = el('label', 'ctl');
  const name = el('span', 'ctl-name', label), val = el('output', 'ctl-val');
  const input = el('input');
  Object.assign(input, { type: 'range', min: String(min), max: String(max), step: String(step) });
  input.addEventListener('input', () => { set(+input.value); val.textContent = fmt(+input.value); });
  root.append(name, val, input);
  const refresh = () => { input.value = String(get()); val.textContent = fmt(get()); };
  refresh();
  return { root, refresh };
}

export function colour(label: string, get: () => [number, number, number], set: (c: [number, number, number]) => void): Control {
  const root = el('label', 'ctl ctl-colour');
  const input = el('input');
  input.type = 'color';
  input.addEventListener('input', () => set(linOf(input.value)));
  root.append(el('span', 'ctl-name', label), input);
  const refresh = () => { input.value = hexOf(get()); };
  refresh();
  return { root, refresh };
}

export function toggle(label: string, get: () => boolean, set: (v: boolean) => void): Control {
  const root = el('label', 'ctl ctl-toggle');
  const input = el('input');
  input.type = 'checkbox';
  input.addEventListener('change', () => set(input.checked));
  root.append(input, el('span', 'ctl-name', label));
  const refresh = () => { input.checked = get(); };
  refresh();
  return { root, refresh };
}

/** one of a few, as a row of printed tabs */
export function choice<T extends string>(label: string, opts: { v: T; label: string }[], get: () => T, set: (v: T) => void): Control {
  const root = el('div', 'ctl ctl-choice');
  if (label) root.append(el('span', 'ctl-name', label));
  const row = el('div', 'seg');
  const btns = opts.map(o => {
    const b = button(o.label, () => { set(o.v); refresh(); }, 'seg-b');
    b.dataset.v = o.v;
    row.append(b);
    return b;
  });
  root.append(row);
  const refresh = () => btns.forEach(b => b.classList.toggle('on', b.dataset.v === get()));
  refresh();
  return { root, refresh };
}

export function section(title: string, ...kids: (HTMLElement | Control)[]): HTMLElement {
  const s = el('section', 'card');
  s.append(el('h3', 'card-h', title));
  for (const k of kids) s.append(k instanceof HTMLElement ? k : k.root);
  return s;
}

let toastTimer = 0;
export function toast(msg: string, ms = 3200) {
  let t = document.getElementById('toast');
  if (!t) { t = el('div', 'toast'); t.id = 'toast'; t.setAttribute('role', 'status'); document.body.append(t); }
  t.textContent = msg;
  t.classList.add('show');
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => t!.classList.remove('show'), ms);
}

/** the piece's own outline, w x h px, as points round it with outward normals: a flat top with small corners and a
 *  round foot, the same border-radius as `.piece` (10px 14px 46% 54% / 10px 12px 70% 64%) */
function pieceOutline(w: number, h: number): { p: [number, number]; n: [number, number] }[] {
  const fit = (a: number, b: number, len: number) => (a + b > len ? len / (a + b) : 1);
  const kx = Math.min(1, fit(10, 14, w), fit(0.54 * w, 0.46 * w, w)), ky = Math.min(1, fit(10, 0.64 * h, h), fit(12, 0.7 * h, h));
  const k = Math.min(kx, ky);
  // corners: centre and radii, swept clockwise from the top left
  const tl = [10 * k, 10 * k], tr = [14 * k, 12 * k], br = [0.46 * w * k, 0.7 * h * k], bl = [0.54 * w * k, 0.64 * h * k];
  const arcs: [number, number, number, number, number][] = [
    [w - tr[0], tr[1], tr[0], tr[1], -Math.PI / 2],
    [w - br[0], h - br[1], br[0], br[1], 0],
    [bl[0], h - bl[1], bl[0], bl[1], Math.PI / 2],
    [tl[0], tl[1], tl[0], tl[1], Math.PI],
  ];
  const pts: [number, number][] = [];
  for (const [cx, cy, rx, ry, a0] of arcs) {
    const m = Math.max(3, Math.round((rx + ry) / 7));
    for (let i = 0; i <= m; i++) { const a = a0 + (i / m) * (Math.PI / 2); pts.push([cx + rx * Math.cos(a), cy + ry * Math.sin(a)]); }
  }
  // even spacing, about 6px apart, so the wobble is the same all the way round
  const len: number[] = [0];
  for (let i = 1; i <= pts.length; i++) { const a = pts[i - 1], b = pts[i % pts.length]; len.push(len[i - 1] + Math.hypot(b[0] - a[0], b[1] - a[1])); }
  const total = len[len.length - 1], n = Math.max(24, Math.round(total / 6)), out: [number, number][] = [];
  for (let i = 0, j = 0; i < n; i++) {
    const d = (i / n) * total;
    while (len[j + 1] < d) j++;
    const a = pts[j], b = pts[(j + 1) % pts.length], t = (d - len[j]) / (len[j + 1] - len[j] || 1);
    out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
  }
  return out.map((p, i) => {
    const a = out[(i + n - 1) % n], b = out[(i + 1) % n], dx = b[0] - a[0], dy = b[1] - a[1], l = Math.hypot(dx, dy) || 1;
    return { p, n: [dy / l, -dx / l] as [number, number] };
  });
}

/** one frame of the ghost: a white band round the whole piece, swelling and thinning in places, puffed up more on top */
function ghostRing(o: ReturnType<typeof pieceOutline>, h: number, k: number): string {
  const n = o.length, f = (v: number) => v.toFixed(1);
  const outer: [number, number][] = [], inner: [number, number][] = [];
  o.forEach(({ p, n: [nx, ny] }, i) => {
    const u = i / n, up = Math.max(0, 1 - p[1] / (h * 0.45));
    const swell = Math.sin(u * 6.28 * 3 + k * 2.1) * 0.5 + Math.sin(u * 6.28 * 5 - k * 1.3 + 1) * 0.35 + Math.sin(u * 6.28 * 2 + k * 0.7) * 0.4;
    const gap = 2.5 + Math.sin(u * 6.28 * 4 + k * 1.9) * 1.2;
    const width = Math.max(1.6, 4.5 + swell * 3 + up * (4 + 3 * Math.sin(u * 6.28 * 7 + k * 2.6)));
    inner.push([p[0] + nx * gap, p[1] + ny * gap]);
    outer.push([p[0] + nx * (gap + width), p[1] + ny * (gap + width)]);
  });
  const ring = (r: [number, number][]) => {
    let d = `M${f(r[0][0])} ${f(r[0][1])}`;
    for (let i = 0; i < r.length; i++) {
      const p0 = r[(i + r.length - 1) % r.length], p1 = r[i], p2 = r[(i + 1) % r.length], p3 = r[(i + 2) % r.length];
      d += `C${f(p1[0] + (p2[0] - p0[0]) / 6)} ${f(p1[1] + (p2[1] - p0[1]) / 6)} ${f(p2[0] - (p3[0] - p1[0]) / 6)} ${f(p2[1] - (p3[1] - p1[1]) / 6)} ${f(p2[0])} ${f(p2[1])}`;
    }
    return d + 'Z';
  };
  return ring(outer) + ring(inner.reverse());
}

/** the white ghost round the piece in hand: the piece's whole outline in white, wobbling thick and thin, blobs rising off the top */
export function ghost(): HTMLElement {
  const g = el('span', 'ghost', `<svg aria-hidden="true"><path fill-rule="evenodd"><animate attributeName="d" dur="2.6s" repeatCount="indefinite"/></path></svg><i class="blob"></i><i class="blob"></i><i class="blob"></i>`);
  let size = '';
  const draw = () => {
    const piece = g.parentElement;
    if (!piece) return;
    const w = piece.offsetWidth, h = piece.offsetHeight;
    if (!w || !h || `${w}x${h}` === size) return;
    size = `${w}x${h}`;
    const o = pieceOutline(w, h), frames = [0, 1.6, 3.2, 4.8].map(k => ghostRing(o, h, k));
    const svg = g.querySelector('svg')!, path = svg.querySelector('path')!;
    svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
    path.setAttribute('d', frames[0]);
    path.querySelector('animate')!.setAttribute('values', [...frames, frames[0]].join(';'));
  };
  const ro = new ResizeObserver(() => { if (!g.isConnected) ro.disconnect(); else draw(); });
  queueMicrotask(() => { if (g.parentElement) ro.observe(g.parentElement); draw(); });
  return g;
}
